import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import OrderForm from './components/OrderForm';
import OrderView from './components/OrderView';
import Uploader from './components/Uploader';
import DailyCommitmentOrders from './components/DailyCommitmentOrders';
import DailyAccount from './components/DailyAccount';
import LeaveCalendar from './components/LeaveCalendar';
import Utilities from './components/Utilities';
import Reports from './components/Reports';
import FinanceDashboard from './components/FinanceDashboard';
import FinancialAnalysisReport from './components/FinancialAnalysisReport';
import TaskKanban from './components/TaskKanban';
import OrderProcessor from './components/OrderProcessor';
import StoreSelectionDialog from './components/StoreSelectionDialog';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { Store } from './types';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          '@media (max-width: 600px)': {
            fontSize: '0.8rem',
            minHeight: 40,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e0e0e0',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
        },
      },
    },
  },
});

// Storage key for selected store
const SELECTED_STORE_KEY = 'selectedStore';

function App() {
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showStoreDialog, setShowStoreDialog] = useState(false);

  useEffect(() => {
    // Check if store is already selected in localStorage
    const savedStore = localStorage.getItem(SELECTED_STORE_KEY);
    if (savedStore) {
      try {
        const store = JSON.parse(savedStore);
        setSelectedStore(store);
      } catch (error) {
        console.error('Error parsing saved store:', error);
        // If parsing fails, show store selection dialog
        setShowStoreDialog(true);
      }
    } else {
      // No store selected, show dialog
      setShowStoreDialog(true);
    }
  }, []);

  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    localStorage.setItem(SELECTED_STORE_KEY, JSON.stringify(store));
    setShowStoreDialog(false);
  };

  const handleChangeStore = () => {
    setShowStoreDialog(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected routes */}
              <Route path="/*" element={
                <ProtectedRoute requiredPermission="view_orders">
                  <Layout selectedStore={selectedStore} onChangeStore={handleChangeStore}>
                    <Routes>
                      <Route 
                        path="/" 
                        element={
                          <ProtectedRoute requiredPermission="create_orders">
                            <OrderForm selectedStore={selectedStore} />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/orders" 
                        element={
                          <ProtectedRoute requiredPermission="view_orders">
                            <OrderView />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/uploader" 
                        element={
                          <ProtectedRoute requiredPermission="view_orders">
                            <Uploader />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/commitment" 
                        element={
                          <ProtectedRoute requiredPermission="view_orders">
                            <DailyCommitmentOrders selectedStore={selectedStore} />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/daily-account" 
                        element={
                          <ProtectedRoute requiredPermission="view_orders">
                            <DailyAccount selectedStore={selectedStore} />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/leave-calendar" 
                        element={
                          <ProtectedRoute requiredPermission="view_orders">
                            <LeaveCalendar />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/utilities" 
                        element={
                          <ProtectedRoute requiredPermission="utilities">
                            <Utilities selectedStore={selectedStore} />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/reports" 
                        element={
                          <ProtectedRoute requiredPermission="item_reports">
                            <Reports />
                          </ProtectedRoute>
                        } 
                      />
                      <Route
                        path="/finance-dashboard"
                        element={
                          <ProtectedRoute requiredPermission="finance_dashboard">
                            <FinanceDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/order-processor"
                        element={
                          <ProtectedRoute requiredPermission="order_processor">
                            <OrderProcessor selectedStore={selectedStore} />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/financial-analysis-report"
                        element={
                          <ProtectedRoute requiredPermission="finance_dashboard">
                            <FinancialAnalysisReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tasks"
                        element={<TaskKanban selectedStore={selectedStore} />}
                      />
                    </Routes>
                  </Layout>
                  <StoreSelectionDialog
                    open={showStoreDialog}
                    onStoreSelect={handleStoreSelect}
                    selectedStore={selectedStore}
                  />
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
